import React, { useCallback, useEffect, useState } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FormBody,
  FormContentContainer,
  BottomActionButton,
  BottomActionArea,
  FullScreenLoader,
  SecondaryActionButton,
} from "@components";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { BankTransactionStatus } from "@interfaces";
import { CheckCircleOutlined } from "@mui/icons-material";
import { useVerifyBankTransferStatus } from "@hooks";
import { tracker } from "@utils/openreplay";
import { CancelOutlined } from "@mui/icons-material";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";

const StyledCheckCircleIcon = styled(CheckCircleOutlined)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.success.main,
}));
const StyledBottomActionButton = styled(BottomActionButton)({
  gap: 6,
});

const StyledCancelIcon = styled(CancelOutlined)(({ theme }) => ({
  fontSize: "46px",
  color: theme.palette.error.main,
}));

export const SuccessfulBankTransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { getEnrolmentStatus } = useEnrolmentStatusContext();
  const {
    data: challengeStatusData,
    isPending: isGettingTransactionStatus,
    error: errorGettingTransactionStatus,
  } = useVerifyBankTransferStatus();

  const onClickNext = useCallback(async () => {
    const requestId = searchParams.get("requestId");
    const queryParams = requestId ? `?requestId=${requestId}` : "";
    await getEnrolmentStatus();
    navigate(
      `/app/registro/firma-electronica-avanzada/crear-pin${queryParams}`,
    );
  }, [navigate, searchParams, getEnrolmentStatus]);

  const handleRetry = useCallback(async () => {
    setIsLoading(true);
    await getEnrolmentStatus();
  }, [getEnrolmentStatus]);

  useEffect(() => {
    if (
      challengeStatusData?.challengeStatus === BankTransactionStatus.APPROVED
    ) {
      tracker?.event("BankTransferChallengeSuccess", {
        source: "SuccessfulBankTransferScreen",
      });
    }
  }, [challengeStatusData?.challengeStatus]);

  useEffect(() => {
    if (errorGettingTransactionStatus) {
      tracker?.event("BankTransferChallengeStatusError", {
        source: "SuccessfulBankTransferScreen",
        error: errorGettingTransactionStatus,
        errorMessage: errorGettingTransactionStatus?.message,
      });
    }
  }, [errorGettingTransactionStatus]);

  if (errorGettingTransactionStatus)
    return (
      <>
        <FormHeader useMekiDocIcon />
        <FormContentContainer>
          <FormBody justifyContent="center">
            <Box
              marginBottom="48px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="16px"
            >
              <StyledCancelIcon />
              <Typography variant="h6" fontWeight="semibold">
                Transferencia cancelada
              </Typography>
            </Box>
            <StyledBottomActionButton
              onClick={handleRetry}
              disabled={isLoading}
            >
              Intentar nuevamente
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null}
            </StyledBottomActionButton>

            <SecondaryActionButton
              onClick={() => navigate("/app/solicitudes")}
              disabled={isLoading}
            >
              Volver a inicio
            </SecondaryActionButton>
          </FormBody>
        </FormContentContainer>
      </>
    );

  if (
    isGettingTransactionStatus ||
    challengeStatusData?.challengeStatus === BankTransactionStatus.PENDING
  )
    return (
      <>
        <FormHeader useMekiDocIcon />
        <FormContentContainer>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Validando transferencia bancaria" />
          </FormBody>
        </FormContentContainer>
      </>
    );

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <Box
            marginBottom="48px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="16px"
          >
            <StyledCheckCircleIcon />
            <Typography variant="h5" fontWeight="semibold">
              Transferencia exitosa
            </Typography>
          </Box>
          <BottomActionArea>
            <BottomActionButton onClick={onClickNext}>
              Continuar
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
